import { z } from "zod";
import { Answer, ExerciseElement, SLIDE_MAX_EXERCISES } from "./exercises.ts";
import { SLIDE_HEIGHT, SLIDE_WIDTH } from "./slide-size.ts";
import { TTS_LINES_HARD_MAX, TtsTranscriptLine } from "./tts-transcript.ts";

/**
 * Слайды урока.
 *
 * Урок это последовательность слайдов, преподаватель листает, ученик следует за
 * ним, пока не пролистает сам (`own-slide.ts` в комнате: своё положение живёт в
 * состоянии экрана и в документ не едет). Слайд состоит из трёх слоёв в ОДНОЙ
 * логической системе координат: подложка, интерактивные элементы, аннотации
 * Excalidraw.
 *
 * Логический размер общий для клиента и сервера. Смысл фиксации: рисунок
 * преподавателя с ноутбука обязан оказаться в том же месте слайда у ученика с
 * телефона. Подгонять координаты под вьюпорт нельзя, иначе «вот здесь ошибка»
 * перестаёт указывать туда, куда показали.
 *
 * ⚠️ Сами числа переехали в лист `./slide-size.ts` (03.09.2026) и отсюда
 * реэкспортируются: второй читатель, геометрия новой коробки, до них по прямой
 * не дотягивается, потому что импорт этого файла замкнул бы кольцо. Разбор
 * причины стоит в самом листе. Для читателей барреля не изменилось ничего.
 */

export { SLIDE_HEIGHT, SLIDE_WIDTH } from "./slide-size.ts";

/**
 * Ключи верхнего уровня в Y.Doc урока.
 *
 * Живут в общем пакете по той же причине, что и BOARD_ELEMENTS_KEY: читают их
 * двое, браузер и сборщик снимков на сервере. Разъехавшиеся строковые литералы
 * дали бы пустой архив при полностью рабочем уроке.
 */
export const SLIDES_KEY = "slides";
export const PRESENTATION_KEY = "presentation";
export const ANNOTATIONS_KEY = "annotations";

/**
 * Второй слой: условия отдельно, ответы отдельно, обе карты по id слайда.
 *
 * ⚠️ Ключи верхнего уровня, а НЕ поля внутри `Y.Map` слайда, и это не вкусовое.
 * `moveSlide` перемещать элемент `Y.Array` не умеет и пересоздаёт слайд через
 * `makeSlide`, который переносит поля поимённо. Любое новое поле внутри слайда
 * потерялось бы при первой же перестановке, молча. Карты по `slideId` о
 * порядке не знают вовсе и перестановку переживают, ровно как `annotations`.
 *
 * Почему условия и ответы разведены, три самостоятельные причины:
 *
 *   1. разные жизненные циклы. Условие меняется раз в урок и целиком, ответ
 *      постоянно. Слитые в один объект, они дают перезапись всего условия на
 *      каждое нажатие клавиши
 *   2. копирование. В заготовку едет `exercises`, и физически нечего забыть
 *      отфильтровать
 *   3. права. Ученик имеет право писать в `answers` и не имеет права писать в
 *      `exercises`, и с разведёнными ключами это проверяемое утверждение
 */
export const EXERCISES_KEY = "exercises";
export const ANSWERS_KEY = "answers";

/**
 * Текст клеток таблиц (3.2 `docs/board-table-design.md`): карта по id элемента
 * таблицы, внутри `Y.Map` по ключу клетки `строка.столбец` со значением
 * `Y.Text`. Структура таблицы лежит в `EXERCISES_KEY`, как у любого задания.
 *
 * ⚠️ Ключ по id ЭЛЕМЕНТА, а не слайда: перенос задания на другой слайд слой не
 * трогает. Почему не в элементе: затирание соседних клеток и надгробие размером
 * с таблицу на каждую букву (причина 1 выше). Почему не в `ANSWERS_KEY` с
 * меткой: у группы ответы общего документа вытесняются из снимка. Пишут и
 * удаляют слой только через доступ `./table-layer.ts`.
 */
export const TABLES_KEY = "tables";

/**
 * Таймер занятия: три дискретных поля, момент окончания, длительность и
 * момент, к которому эта длительность относится.
 *
 * ⚠️ **В документе лежит дискретное, а не тикающее** (правило 1 раздела 5.3
 * CLAUDE.md). Все три поля пишутся одной транзакцией при запуске и снимаются
 * при остановке, то есть за запуск это одно событие карты. Оставшиеся секунды в `Y.Map`
 * были бы потоком надгробий длиной в занятие, ровно тем, от чего защищает
 * правило: обратный отсчёт каждый клиент считает у себя своим таймером.
 *
 * ⚠️ **Момент приходит с часов ТОГО, КТО НАЖАЛ, а читается по часам того, кто
 * смотрит, и часы у них разные.** Расхождение видно как разные секунды на двух
 * экранах, и для классного таймера это терпимо: он про «сколько ещё пишем», а
 * не про судейский секундомер. Настоящее лечение стоило бы сверки часов с
 * сервером на каждом клиенте, и заводить её ради таймера, который ничего не
 * запрещает, незачем. Записано здесь, потому что решение принимается здесь.
 *
 * ⚠️ Ничего не ЗАПРЕЩАЕТ. Ноль это надпись на экране, а не замок: ни листание,
 * ни ответы к таймеру не привязаны. Оборвать набираемый ответ по таймеру значит
 * потерять работу ученика.
 *
 * Отдельным ключом верхнего уровня, а не полем `presentation`: у него своё
 * поведение и свои читатели, а сложенные в одну карту два разных дела однажды
 * начинают перерисовывать друг друга.
 */
export const TIMER_KEY = "timer";

/** Момент окончания, миллисекунды эпохи. Отсутствие поля значит «не идёт». */
export const TIMER_ENDS_AT_FIELD = "endsAt";

/**
 * Длительность запуска, целые секунды. Нужна одному: доле заливки в полосе
 * таймера (решение владельца 01.10.2026, отсчёт в полосе шапки).
 *
 * ⚠️ **Совместимость со старыми вкладками односторонняя, и это названо.**
 * Вкладка до этой правки читает только `endsAt` и лишних полей не замечает, но
 * и пишет только его: при запуске ставит `endsAt`, при остановке снимает
 * `endsAt`. Длительность от прошлого нового запуска переживает её остановку и
 * запуск, и без проверки ниже заливка считалась бы от чужого числа молча.
 */
export const TIMER_SECONDS_FIELD = "seconds";

/**
 * Момент окончания, к которому относится `seconds`: пишется тем же числом, что
 * `endsAt`.
 *
 * ⚠️ **Третье поле, а не проверка правдоподобия длительности.** Эвристика
 * «остаток не больше длительности» ловила хвост от старой вкладки только в одну
 * сторону. С этим полем вопрос двоичный и без часов: длительность принимается,
 * только если `secondsFor === endsAt`, а старая вкладка, переписав `endsAt`,
 * это равенство разрушает всегда (моменты окончания двух запусков с
 * миллисекундной точностью не совпадают). Не принятая длительность значит
 * полосу без заливки, время в подписи при этом верное.
 */
export const TIMER_SECONDS_FOR_FIELD = "secondsFor";

/**
 * Поле с текущим слайдом. Хранится ИМЕННО id, а не индекс: вставка и удаление
 * слайда не должны швырять обоих участников по уроку.
 *
 * Дискретное значение, меняется несколько раз за занятие, поэтому ему место в
 * документе. Ничего непрерывного (курсор, будущая позиция воспроизведения)
 * сюда класть нельзя: каждая запись это надгробие, а это прямая дорога в
 * yjs#741 (раздел 5.3 CLAUDE.md).
 */
export const CURRENT_SLIDE_FIELD = "currentSlideId";

/**
 * Поле с крупным планом: что занимает всю рабочую площадь комнаты.
 *
 * Хранимая форма это строка `board`, `camera:<userId>` или `screen:<userId>`;
 * разбор и правила живучести в `apps/web/src/features/room/room-closeup.ts`.
 * Отсутствие поля значит доску, поэтому и старый документ, и вкладка старше
 * выкатки читаются сами собой.
 *
 * ⚠️ **Значение ДИСКРЕТНОЕ и ОБЩЕЕ, и только поэтому ему здесь место** (правило
 * 3 раздела 5.3 CLAUDE.md): преподаватель переключает план несколько раз за
 * занятие, то есть это единицы записей. Своё положение ученика («вернулся к
 * доске у себя») сюда не едет ни одним путём: это свойство экрана, ровно как у
 * `own-slide.ts`.
 *
 * ⚠️ **Крупный план НИКОГДА не привязывается к активному говорящему** и вообще
 * ни к чему, что меняется само. У LiveKit есть готовый `activeSpeakers`, и
 * автопереключение на говорящего это очевидная следующая доработка: она даёт
 * запись в документ на КАЖДУЮ смену голоса, то есть поток надгробий и прямую
 * дорогу в yjs#741 (то же правило 3 раздела 5.3 CLAUDE.md). Запрет назван здесь
 * именем, чтобы его прочитал тот, кто соберётся эту доработку делать, а не тот,
 * кто потом будет разбирать четырёхгигабайтный документ.
 */
export const CLOSEUP_FIELD = "closeup";

/**
 * Поле пера: разрешено ли ученикам писать на общей доске занятия.
 *
 * Хранимая форма это строка `on`; всё остальное, включая отсутствие поля,
 * читается как «нельзя». Значит и старый документ, и вкладка старше выкатки
 * начинают с закрытой доски, а не с открытой, и умолчание получается безопасным
 * само собой, без единой строки перехода.
 *
 * ⚠️ **Решение владельца 10.09.2026, три части сразу:** доска закрыта ученику по
 * умолчанию (и в паре тоже, где до этого дня он писал всегда), рычаг ОДИН на
 * комнату, а не по именам, и живёт разрешение до конца занятия. Последнее и есть
 * причина, по которой поле лежит здесь, а не колонкой в базе: документ доски
 * ротируется по границе занятия (правило 1 раздела 5.3 CLAUDE.md), то есть срок
 * жизни разрешения задан устройством хранения, а не уборщиком.
 *
 * ⚠️ **Значение дискретное и общее**, как крупный план строкой выше: за занятие
 * это единицы записей, поэтому правило 3 раздела 5.3 не нарушено.
 *
 * ⚠️ **Поле читают ТРОЕ, и это единственный источник правды про перо:** холст у
 * ученика (`viewModeEnabled`), правило видимости курсоров и сервер синхронизации,
 * который поднимает область записи на живом соединении
 * (`apps/collab/src/pen-scope.ts`). Своей копии правила («я ученик пары, значит
 * мне можно») нет ни у кого намеренно: она разъехалась бы с сервером молча.
 */
export const PEN_FIELD = "studentPen";

/** Единственное значение поля пера, означающее «писать можно». */
export const PEN_ON = "on";

/**
 * Значение «писать нельзя».
 *
 * ⚠️ Заведено ради ЗАПРЕТА, а не ради полноты словаря: отбирая перо, поле
 * перезаписывают этим значением, а не снимают. Разбор в шапке `setStudentPen`
 * у web, коротко это про то, что снятое поле уезжает соседям не всегда.
 */
export const PEN_OFF = "off";

/**
 * Разбор поля пера. Снисходительный: всё, кроме точного `on`, это «нельзя».
 *
 * ⚠️ Отдельной функцией, а не сравнением на месте, ровно потому, что читателей
 * трое и живут они в трёх процессах. Сравнение, размноженное по местам, однажды
 * разъедется в одном из них, и разъедется в сторону разрешения.
 */
export function studentPenOn(value: unknown): boolean {
  return value === PEN_ON;
}

/**
 * Калькулятор ученику: можно ли ему открыть калькулятор на уроке (просьба Влада
 * 29.09.2026, проект `docs/room-calculator-design.md`).
 *
 * ⚠️ **Решение то же, что у пера строкой выше, и по тем же причинам:** рычаг
 * ОДИН на комнату, а не по именам, живёт до конца занятия и по умолчанию
 * выключен. Преподавателю калькулятор доступен на уроке математики, а вне её,
 * пока рычаг выдан (`docs/room-math-tools-design.md`, 1.3); поле про него
 * самого не говорит ничего. Срок жизни задан ротацией документа по границе занятия, уборщика нет.
 *
 * ⚠️ **Читатель у поля ОДИН, клиент**, и в этом отличие от пера. Сервер поле не
 * читает ни одной строкой: рубеж только клиентский, у пары и у группы. Ученик с
 * консолью получит калькулятор в обход рычага, и это принято: калькулятор
 * ничего не хранит, ничего чужого не показывает и никуда не пишет (раздел 3.1
 * проекта).
 *
 * ⚠️ **Цифр калькулятора в документе нет и быть не должно.** Ни набранного
 * числа, ни выражения, ни признака «калькулятор открыт»: это личное состояние
 * одной вкладки, а в документе оно было бы ещё и потоком надгробий (правило 3
 * раздела 5.3 CLAUDE.md). Держится замком
 * `apps/web/src/features/calculator/calc-private.test.ts`.
 */
export const CALC_FIELD = "studentCalc";

/**
 * Единственное значение поля калькулятора, означающее «ученику можно».
 *
 * ⚠️ Свои константы, а не `PEN_ON` повторно, хотя строки совпадают: разбор
 * калькулятора не должен зависеть от того, что однажды поменяют у пера.
 */
export const CALC_ON = "on";

/**
 * Значение «ученику нельзя». Запрет пишется значением, а не снятием поля, по
 * той же причине, что у пера: снятое поле доезжает до соседей не всегда, и
 * забранный калькулятор остался бы у ребёнка.
 */
export const CALC_OFF = "off";

/** Разбор поля калькулятора. Снисходительный: всё, кроме точного `on`, это «нельзя». */
export function studentCalcOn(value: unknown): boolean {
  return value === CALC_ON;
}

/**
 * id первого слайда детерминирован, и это не мелочь.
 *
 * Заводить первый слайд может любой клиент, а подключиться двое могут
 * одновременно. С случайным id получилось бы два первых слайда, с общим
 * получается одна и та же запись: аннотации сходятся поэлементно (ключ это id
 * элемента), а дубль в списке слайдов схлопывает чтение. Дальше слайды
 * добавляет только преподаватель, там гонки нет и id случайный.
 */
export const FIRST_SLIDE_ID = "slide-1";

/**
 * Виды клетчатой подложки.
 *
 * Четыре, и каждый под свою работу: `square` для математики, `dots` для
 * геометрии и для слайдов с текстом (клетка мешает читать), `lined` для
 * прописей и РКИ, `axes` координатная плоскость с осями и подписями.
 *
 * Пятого вида здесь быть не должно без живого пользователя, который его
 * попросил: каждый вид это ветка в отрисовке и кнопка на экране, который на
 * телефоне в портрете и так тесен.
 */
export const SLIDE_GRID_STYLES = ["square", "dots", "lined", "axes"] as const;
export const SlideGridStyle = z.enum(SLIDE_GRID_STYLES);
export type SlideGridStyle = z.infer<typeof SlideGridStyle>;

/** Шаг клетки по умолчанию, в логических единицах слайда. */
export const SLIDE_GRID_DEFAULT_STEP = 32;

/**
 * Подложка слайда: пусто, клетка или картинка из рабочего пространства
 * преподавателя.
 *
 * Картинка это ровно та механика, которой пользуется импорт документов: каждая
 * нарезанная страница становится такой же подложкой, и импорту не пришлось
 * добавлять сюда ничего. Поэтому здесь id файла, а не адрес: адрес живёт минуты
 * (подписанная ссылка), а слайд лежит годами.
 *
 * Размеры кадра приезжают из строки файла и могут быть null: у обычной картинки
 * их заполняет замер в браузере, у импортированной страницы их знает сервер,
 * потому что он её и нарисовал. Отрисовке они не нужны (`object-fit: contain`),
 * нужны раскладке, чтобы не ждать загрузки картинки.
 *
 * Клетка нарисована нами и файла не имеет вовсе: ноль байт по проводу, ноль
 * квоты, ноль строк в media_file и ноль работы уборщику корзины. Картинка
 * клетки 1600x900 весила бы десятки килобайт и мылила бы на ретине, а
 * SVG-паттерн одинаково резок при любом масштабе.
 *
 * ⚠️ Это дискриминированный юнион, и неизвестный `kind` роняет разбор ВСЕГО
 * массива слайдов, а не одного слайда. Отсюда порядок выкатки: api и collab
 * узнают новый вид РАНЬШЕ, чем web получает кнопку, которая его пишет. Иначе
 * старый API отвечает 400 на сохранение заготовки, а старый collab отказывает
 * в засеве урока целиком (`PlanSlidesWire.safeParse` в collab/seed-request.ts).
 */
export const SlideBackground = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }),
  z.object({
    kind: z.literal("image"),
    fileId: z.string().uuid(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
  }),
  z.object({
    kind: z.literal("grid"),
    style: SlideGridStyle,
    /** Шаг в логических единицах слайда. 32 это примерно школьная клетка. */
    step: z.number().int().min(8).max(160).default(SLIDE_GRID_DEFAULT_STEP),
    /**
     * Начало координат. Нужно `axes`, остальные стили по нему выравнивают
     * сетку, чтобы смена стиля не сдвигала клетку под уже нарисованным.
     *
     * Целые намеренно: дробные координаты подложке не нужны, а `int` заодно
     * отсекает NaN и бесконечность, которые в отрисовке дали бы пустой экран.
     */
    originX: z
      .number()
      .int()
      .default(SLIDE_WIDTH / 2),
    originY: z
      .number()
      .int()
      .default(SLIDE_HEIGHT / 2),
  }),
]);
export type SlideBackground = z.infer<typeof SlideBackground>;

export const EMPTY_BACKGROUND: SlideBackground = { kind: "none" };

export type SlideGridBackground = Extract<SlideBackground, { kind: "grid" }>;

/**
 * Клетка со значениями по умолчанию.
 *
 * Живёт здесь по той же причине, что и `emptyPlanSlide`: заводят её двое,
 * редактор заготовки и комната урока, а разъехавшиеся литералы дали бы
 * подложку с разным шагом в заготовке и в уроке, собранном из неё.
 *
 * Значения пишутся явно, а не оставляются на умолчания схемы: слайд лежит
 * годами, и он обязан сам про себя всё рассказывать, даже если умолчание в
 * схеме однажды поменяется.
 */
export function gridBackground(
  style: SlideGridStyle,
  step: number = SLIDE_GRID_DEFAULT_STEP,
): SlideGridBackground {
  return {
    kind: "grid",
    style,
    step,
    originX: SLIDE_WIDTH / 2,
    originY: SLIDE_HEIGHT / 2,
  };
}

/**
 * Ролик или звук, привязанный к слайду.
 *
 * Здесь только дискретное: какой файл стоит на слайде. Позиции
 * воспроизведения тут нет и быть не может, она едет через awareness. Каждая
 * запись непрерывно меняющегося значения в Y.Map это надгробие, ровно та
 * причина, по которой в документе не живут курсоры (yjs#741, раздел 5.3
 * CLAUDE.md).
 *
 * `kind` дублирует то, что и так знает строка файла, намеренно: слайд обязан
 * знать, чем себя рисовать, ещё до того, как файл найден, а он может быть уже
 * удалён из каталога.
 */
export const SlideMedia = z.object({
  fileId: z.string().uuid(),
  kind: z.enum(["video", "audio"]),
  /**
   * Расшифровка звука КОПИЕЙ, для режима «читать и слушать» у ученика.
   *
   * ⚠️ **Копия, а не ссылка на файл, и это решение, а не срез угла** (пункт 10
   * порядка работ, docs/tts-design.md). Признак «показывать текст» живёт на
   * слайде, а слайд это содержимое документа, а не строка в базе: ручка,
   * отдающая текст ученику, была бы обязана заглянуть в документ урока и
   * убедиться, что показ включён, то есть проверять права по данным, которых у
   * неё нет. С копией вопрос снимается целиком: **пока преподаватель не нажал,
   * текста в слайде нет вовсе**, и отдать его ученику неоткуда даже по ошибке.
   *
   * ⚠️ **Необязательное НАВСЕГДА, а не до релиза**, ровно как `media` у слайда
   * и `exercises` у снимка: заготовки и снимки архива, снятые раньше, обязаны
   * открываться без миграции, и «поля нет» у них означает «текст не
   * показывали», а не «мы его потеряли».
   *
   * Цена копии названа вслух: несколько килобайт в слайде и то, что текст не
   * обновится, если файл переозвучат. Для расшифровки это верно по существу,
   * она описывает ТОТ звук, который лежит на слайде.
   *
   * Потолок тот же, что у самой озвучки: длиннее шестидесяти реплик трека у нас
   * не бывает по построению, а разбор чужой записи из будущего формата не имеет
   * права раздуться в мегабайты.
   */
  transcript: z.array(TtsTranscriptLine).max(TTS_LINES_HARD_MAX).optional(),
});
export type SlideMedia = z.infer<typeof SlideMedia>;

/**
 * Слайд в снимке архива: собственные элементы Excalidraw плюс подложка.
 *
 * Элементы zod-схемой не описываем по той же причине, что и в плоском снимке:
 * OrderedExcalidrawElement это сотни строк типов, меняющихся с каждым релизом
 * @next, и схема ломалась бы на каждом обновлении доски, ничего при этом не
 * защищая.
 *
 * `media` необязательное, и это обязательное условие: снимки, снятые до
 * появления медиа, обязаны продолжать открываться без миграции.
 */
export const SlideSnapshot = z.object({
  id: z.string().min(1),
  background: SlideBackground,
  elements: z.array(z.record(z.string(), z.unknown())),
  media: SlideMedia.nullish(),
  /**
   * Слой 2, СКОМПИЛИРОВАННЫЙ. Ответов здесь нет по построению (exercises.ts).
   *
   * Необязательно навсегда, а не до контракта: снимки архива, снятые до этой
   * фичи, останутся в базе навсегда, и «поля нет» у них означает «упражнений
   * правда не было», а не «мы их потеряли».
   */
  exercises: z.array(ExerciseElement).max(SLIDE_MAX_EXERCISES).nullish(),
  /**
   * Ответы ученика по id упражнения. Бывают ТОЛЬКО в снимке архива: в
   * заготовке их нет по смыслу, и `savePlanSlides` их стирает.
   */
  answers: z.record(z.string(), Answer).nullish(),
});
export type SlideSnapshot = z.infer<typeof SlideSnapshot>;

const UTF8 = new TextEncoder();

/**
 * Вес уже сериализованных слайдов по проводу: байты UTF-8, как их считает
 * `bodyLimit` Fastify и чтение тела в collab.
 *
 * ⚠️ Длина строки JavaScript (UTF-16) это не мера: кириллица весит в ней вдвое
 * меньше, чем по проводу. Мера в SQL (`octet_length(slides::text)`) тоже не
 * она: текст `jsonb` другой. Отдельная функция от строки нужна сохранению:
 * там `JSON.stringify` считается один раз, мерится и уходит в запись той же
 * строкой (`docs/pdf-append-design.md`, 3.4 п. 5).
 */
export function wireJsonBytes(json: string): number {
  return UTF8.encode(json).byteLength;
}

/** Вес слайдов по проводу (см. `wireJsonBytes`). */
export function slidesWireBytes(slides: readonly SlideSnapshot[]): number {
  return wireJsonBytes(JSON.stringify(slides));
}

/**
 * Элемент слоя 2 в том минимуме, который нужен для сбора файлов.
 *
 * `kind` здесь не ради дела, а ради типов: без общего поля структурная
 * совместимость с `ExerciseElement` не выводится, потому что тип из одних
 * необязательных полей TypeScript считает «слабым» и требует хоть одного
 * пересечения.
 */
export interface SlideExerciseFileRef {
  kind: string;
  fileId?: string | null | undefined;
  /** Привязка к странице (`./page-link.ts`). Читает только `slidePageFileIds`. */
  page?: { fileId: string } | null | undefined;
}

/** Слайд в том минимуме, который нужен, чтобы собрать список файлов. */
export interface SlideFileRefs {
  background: SlideBackground;
  media?: SlideMedia | null | undefined;
  exercises?: readonly SlideExerciseFileRef[] | null | undefined;
}

/**
 * Все файлы, которые стоят на слайдах, без повторов.
 *
 * Читают это двое: сохранение заготовки и засев урока, оба пишут по нему
 * таблицу связей «файл используется здесь». Спрашивать то же самое у Y.Doc из
 * API дорого и ненадёжно, поэтому связь пишется там же, где копируются слайды.
 */
export function slideFileIds(slides: readonly SlideFileRefs[]): string[] {
  const ids = new Set<string>();
  for (const slide of slides) {
    if (slide.background.kind === "image") ids.add(slide.background.fileId);
    if (slide.media) ids.add(slide.media.fileId);
    /**
     * ⚠️ Файлы слоя 2 обязаны попасть сюда, иначе доступ ученика не получится
     * сам собой. Единственный такой файл сегодня это SVG графика, и без этого
     * цикла у преподавателя он виден (он владелец), а у ученика «картинка
     * недоступна», причём заметит это ученик посреди урока. Одна правка чинит
     * оба пути сразу: по этой функции пишутся и `lesson_media_ref`, и
     * `plan_media_ref`.
     */
    for (const exercise of slide.exercises ?? []) {
      const fileId = exercise.fileId;
      if (typeof fileId === "string" && fileId.length > 0) ids.add(fileId);
    }
  }
  return [...ids];
}

/**
 * Файлы страниц, к которым привязаны задания слайдов, без повторов
 * (решение 15 `docs/scan-extraction-design.md`).
 *
 * ⚠️ **Отдельно от `slideFileIds`, и это граница прав, а не порядок кода.**
 * `slideFileIds` пишет и `plan_media_ref`, и `lesson_media_ref`, то есть
 * раздаёт ученику право читать файл. Картинка страницы ученику не положена
 * (раздел 8 проекта), а заготовке знать её надо: без связи удаление картинки
 * прошло бы без предупреждения. Поэтому объединение с меткой `slide` пишут
 * только сохранение и дублирование заготовки, а урок эту функцию не зовёт.
 */
export function slidePageFileIds(slides: readonly SlideFileRefs[]): string[] {
  const ids = new Set<string>();
  for (const slide of slides) {
    for (const exercise of slide.exercises ?? []) {
      const fileId = exercise.page?.fileId;
      if (typeof fileId === "string" && fileId.length > 0) ids.add(fileId);
    }
  }
  return [...ids];
}

/**
 * `fileId` живых картинок среди ЭЛЕМЕНТОВ доски.
 *
 * Excalidraw хранит картинку в двух местах: элемент типа `image` с полем
 * `fileId` и отдельную карту двоичных файлов. Байты у нас лежат в медиатеке, а
 * в элементе едет только идентификатор (`docs/room-feedback-fixes.md`, раздел
 * 2), и вот он.
 *
 * Принимает `unknown[]`, потому что зовётся с трёх сторон с разными типами: в
 * комнате это сцена Excalidraw, в архиве снимок из базы, на сервере слайды
 * заготовки. Схемой элементы не валидируются намеренно (см. `board.ts`), и
 * притворяться, что тип известен, было бы враньём.
 *
 * Повторы СОХРАНЯЮТСЯ: одна и та же картинка на слайде дважды это норма
 * (копипаст), а тому, кто считает «сколько всего картинок на доске», разница
 * важна. Кому нужен набор, сворачивает его сам.
 */
export function boardImageIds(elements: readonly unknown[]): string[] {
  const out: string[] = [];
  for (const raw of elements) {
    if (typeof raw !== "object" || raw === null) continue;
    const el = raw as { type?: unknown; fileId?: unknown; isDeleted?: unknown };
    if (el.type !== "image" || el.isDeleted === true) continue;
    if (typeof el.fileId === "string" && el.fileId !== "") out.push(el.fileId);
  }
  return out;
}

/**
 * То же самое по всем слайдам разом, без повторов.
 *
 * ⚠️ Существует ради материализации урока из заготовки. Она собирала права по
 * СЛАЙДАМ (`slideFileIds`), то есть про подложку и медиа знала, а про картинку,
 * лежащую прямо на доске, не знала вовсе: элемент доезжал до урока, а право на
 * чтение файла нет, и ученик видел серую заглушку ровно там, где мы её чинили.
 */
export function slideBoardImageIds(
  slides: readonly { elements?: readonly unknown[] | null | undefined }[],
): string[] {
  const ids = new Set<string>();
  for (const slide of slides) {
    for (const fileId of boardImageIds(slide.elements ?? [])) ids.add(fileId);
  }
  return [...ids];
}
